<script setup>
import { Link } from '@inertiajs/vue3'

defineProps({
  label:  { type: String, required: true },
  value:  { type: [String, Number], required: true },
  unit:   { type: String, default: '' },
  delta:  { type: Number, default: null },
  live:   { type: Boolean, default: false },
  period: { type: String, default: '' },
  href:   { type: String, default: '' },
  accent: { type: Boolean, default: true },
  pulse:  { type: Boolean, default: false },
})
</script>

<template>
  <component
    :is="href ? Link : 'div'"
    :href="href || undefined"
    class="stat-card"
    :class="[href ? 'is-link' : '', pulse ? 'pulse-once' : '']"
  >
    <!-- Label row -->
    <div class="flex items-center gap-2">
      <span class="mono text-[0.6875rem] uppercase tracking-[0.08em]" style="color: var(--muted);">
        {{ label }}<span v-if="period" class="normal-case"> · {{ period }}</span>
      </span>
      <span v-if="live" class="live-pill">LIVE</span>
    </div>

    <!-- Value + unit -->
    <div class="flex flex-col gap-0.5 min-w-0">
      <span
        class="font-display leading-[1.05] tracking-[-0.02em] whitespace-nowrap overflow-hidden text-ellipsis"
        style="font-size: 2rem; color: var(--ink); font-weight: 400;"
      >{{ value }}</span>
      <span
        v-if="unit"
        class="text-[0.6875rem] whitespace-nowrap"
        style="color: var(--muted);"
      >{{ unit }}</span>
    </div>

    <!-- Delta -->
    <span
      v-if="delta !== null && delta !== undefined"
      class="mono text-[0.6875rem]"
      :style="{ color: delta >= 0 ? 'var(--ok)' : 'var(--danger)' }"
    >
      {{ delta >= 0 ? '↑' : '↓' }} {{ Math.abs(delta) }}%
    </span>

    <!-- Accent rule -->
    <div
      v-if="accent"
      class="accent-bar"
    />
  </component>
</template>

<style scoped>
.stat-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 118px;
  transition: border-color 150ms, box-shadow 150ms, transform 120ms;
}
.stat-card.is-link { cursor: pointer; }
.stat-card.is-link:hover {
  border-color: var(--lineStrong);
  box-shadow: 0 8px 20px color-mix(in oklab, var(--ink) 6%, transparent);
}
.live-pill {
  font-size: 0.5625rem;
  padding: 1px 5px;
  border-radius: 999px;
  font-weight: 600;
  background: var(--ok);
  color: white;
  letter-spacing: 0.05em;
}
.accent-bar {
  height: 2px;
  width: 28px;
  background: var(--accent);
  border-radius: 999px;
  margin-top: auto;
}
</style>
